import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import ReviewButtons from 'garaje/components/approvals/screeners/review-buttons';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type ScreenerResult from 'garaje/models/screener-result';
import type { IdScanningFaceBox, IdScanningReport } from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import { squareCrop } from 'garaje/utils/face-crop';
import { idDocumentCaption } from 'garaje/utils/id-document-type';

// Shared by both photos so the frame and crop plumbing cannot drift apart.
const PhotoPanel: TOC<{
  Args: {
    src: string | null;
    alt: string;
    /** Inline crop transform; absent falls back to filling the frame. */
    style: SafeString | null;
    onLoad: (event: Event) => void;
    caption: string;
  };
  Element: HTMLElement;
}> = <template>
  <figure class="m-0 flex flex-col items-center shrink-0" ...attributes>
    <div class="w-60 h-60 rounded-lg overflow-hidden bg-carbon-10">
      {{#if @src}}
        <img
          data-private
          data-test-compare-photos-image
          src={{@src}}
          alt={{@alt}}
          class={{unless @style "w-full h-full object-cover"}}
          style={{@style}}
          {{on "load" @onLoad}}
        />
      {{/if}}
    </div>
    <figcaption class="text-base font-medium mt-4" data-test-compare-photos-caption>{{@caption}}</figcaption>
  </figure>
</template>;

interface ComparePhotosModalSignature {
  Args: {
    model: GuestWithVisitApproval;
    result: ScreenerResult;
    documentFaceBox: IdScanningFaceBox | null | undefined;
    visitorFaceBox: IdScanningFaceBox | null | undefined;
    approveTask: Task<void, []>;
    denyTask?: Task<void, []>;
    onClose: () => void;
  };
}

export default class ComparePhotosModal extends Component<ComparePhotosModalSignature> {
  /** Inline crop transforms, set once each image's natural size is known. */
  @tracked documentImageStyle: SafeString | null = null;
  @tracked visitorImageStyle: SafeString | null = null;

  get visitorPhotoUrl(): string | null {
    const model = this.args.model;
    if ('photoUrl' in model && model.photoUrl) return model.photoUrl;
    if ('thumbnails' in model) return model.thumbnails?.original ?? model.thumbnails?.large ?? null;
    return null;
  }

  /**
   * Front image of the scanned ID — the image the face check ran against, and the one
   * the report's document face box is relative to. Only Veriff/ID-scan documents carry
   * externalAttachments, so the first "Front side" hit is the ID scan.
   */
  get documentImageUrl(): string | null {
    const documents = this.args.model.visitorDocuments?.slice() ?? [];
    for (const document of documents) {
      const front = document.externalAttachments?.find((attachment) => attachment.type === 'Front side');
      if (front) return front.url;
    }
    return null;
  }

  get documentCaption(): string {
    const report = this.args.result.report as IdScanningReport | undefined;
    return idDocumentCaption(report?.document?.type);
  }

  /**
   * Position a loaded image inside its square viewport so the face box fills it
   * (padded + clamped). Without a box the image stays on its object-cover fallback.
   */
  #cropStyle(event: Event, box: IdScanningFaceBox | null | undefined): SafeString | null {
    if (!box) return null;

    const image = event.target as HTMLImageElement;
    const { naturalWidth, naturalHeight } = image;
    const viewport = image.parentElement?.clientWidth;
    if (!naturalWidth || !naturalHeight || !viewport) return null;

    const { x, y, side } = squareCrop(box, naturalWidth, naturalHeight);
    const scale = viewport / side;
    return htmlSafe(
      `width: ${naturalWidth * scale}px; height: ${naturalHeight * scale}px; ` +
        `margin-left: -${x * scale}px; margin-top: -${y * scale}px; max-width: none;`,
    );
  }

  handleDocumentImageLoad = (event: Event): void => {
    this.documentImageStyle = this.#cropStyle(event, this.args.documentFaceBox);
  };

  handleVisitorImageLoad = (event: Event): void => {
    this.visitorImageStyle = this.#cropStyle(event, this.args.visitorFaceBox);
  };

  <template>
    {{! Two 240px photos plus the design's gap need more than the 500px .ember-modal-dialog cap. }}
    <ModalDialog
      @clickOutsideToClose={{true}}
      @translucentOverlay={{true}}
      @containerClass="ember-modal-dialog !max-w-[600px]"
      @onClose={{@onClose}}
    >
      <FocusTrapper>
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="compare-photos-modal-title"
          class="w-full p-8"
          data-test-compare-photos-modal
        >
          <header class="flex items-start justify-between">
            <h3 id="compare-photos-modal-title" class="text-xl font-bold mb-0">Compare photos</h3>
            <button
              data-test-compare-photos-close
              class="btn btn--small btn--outline btn--cancel"
              type="button"
              {{on "click" @onClose}}
            >
              <span aria-hidden="true">&times;</span>
              <span class="sr-only">Close</span>
            </button>
          </header>

          <p class="text-sm text-carbon-60 mt-3 mb-0">
            The visitor photo captured at sign-in doesn’t appear to match the photo on this visitor’s government ID.
            Please review and approve or deny the entry.
          </p>

          <div class="flex flex-wrap justify-center gap-12 mt-8">
            <PhotoPanel
              data-test-compare-photos-visitor
              @src={{this.visitorPhotoUrl}}
              @alt="Visitor"
              @style={{this.visitorImageStyle}}
              @onLoad={{this.handleVisitorImageLoad}}
              @caption="From visitor photo"
            />
            <PhotoPanel
              data-test-compare-photos-document
              @src={{this.documentImageUrl}}
              @alt="ID document portrait"
              @style={{this.documentImageStyle}}
              @onLoad={{this.handleDocumentImageLoad}}
              @caption={{this.documentCaption}}
            />
          </div>

          {{! Mock order: Deny left, Approve right — ReviewButtons renders Approve first, so reverse. }}
          <div class="flex justify-end mt-8">
            <div class="w-64 [&>div]:flex-row-reverse">
              <ReviewButtons
                @approveTask={{@approveTask}}
                @denyTask={{@denyTask}}
                @result={{@result}}
                @model={{@model}}
                @onApprove={{@onClose}}
                @onDeny={{@onClose}}
              />
            </div>
          </div>
        </div>
      </FocusTrapper>
    </ModalDialog>
  </template>
}
